import React, { useState, useEffect } from "react";
import { observer } from "mobx-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
//css
import styles from "./sideBar.module.css";

//icons
import { Badge } from "@mui/material";
import TableChartIcon from "@mui/icons-material/TableChart";
import SettingsIcon from "@mui/icons-material/Settings";
import CreateIcon from "@mui/icons-material/Create";
import IsoIcon from "@mui/icons-material/Iso";
import ViewInArOutlinedIcon from "@mui/icons-material/ViewInArOutlined";
import FactoryIcon from "@mui/icons-material/Factory";
import InsertChartIcon from "@mui/icons-material/InsertChart";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import MapIcon from "@mui/icons-material/Map";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import DvrIcon from "@mui/icons-material/Dvr";
import HistoryIcon from "@mui/icons-material/History";

//store
import commonStore from "../Store/marco/commonStore";
import boundarySettingsStore from "../BoundarySettings/store/boundarySettingsStore";

//customIcon
const OverviewIcon = () => {
  return (
    <Badge
      overlap="circular"
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      badgeContent={
        <RemoveRedEyeIcon
          sx={{ bgcolor: "#182350", borderRadius: "50%", fontSize: "14px" }}
        />
      }
    >
      <FactoryIcon />
    </Badge>
  );
};

const QualityIcon = () => {
  return (
    <Badge
      overlap="circular"
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      badgeContent={
        <SettingsIcon
          sx={{ bgcolor: "#182350", borderRadius: "50%", fontSize: "14px" }}
        />
      }
    >
      <TableChartIcon />
    </Badge>
  );
};

const UncertaintyIcon = () => {
  return (
    <Badge
      overlap="circular"
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      badgeContent={
        <CreateIcon
          fontSize="small"
          // color="success"
          sx={{ bgcolor: "#182350", borderRadius: "50%", fontSize: "14px" }}
        />
      }
    >
      <IsoIcon />
    </Badge>
  );
};

const SideBarv4 = observer(({ authenticated }) => {
  const cmStore = commonStore;
  const [selectedName, setSelectedName] = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation("common");

  //store
  const { role, stopRoute, _handleSetPendingUrl, pendingUrl } =
    commonStore;
  const {
    isBoundaryEdit,
    isReportEdit,
    isMaterialityEdit,
    _handleSetIsShowConfirmModal,
  } = boundarySettingsStore;

  const boundaryUnsavedChanges =
    isBoundaryEdit || isReportEdit || isMaterialityEdit;

  //點擊判斷
  const handleClick = (url, name) => {
    setSelectedName(name);
    navigate(url);
  };

  const handleItemClick = (v) => {
    const intercepted = cmStore.tryLeave(() => {
      handleClick(v.url, v.name);
    });

    // ⭐ 如果已經被 tryLeave 接管，就不要跑舊邏輯
    if (intercepted) return;

    console.log("stopRoute", stopRoute);
    if (!stopRoute) {
      handleClick(v.url, v.name);
    } else if (boundaryUnsavedChanges) {
      _handleSetIsShowConfirmModal(true);
    }
  };

  // 新增功能只需要兩個步驟：
  // 1. 在 menuItems 中定義新項目
  // 2. 在對應角色的 roleMenus 中加入 key

  // 所有菜單項目定義
  const menuItems = {
    home: {
      icon: <i className="bi bi-house-fill"></i>,
      name: "Landing Page",
      url: "/home",
    },
    organizationalStructure: {
      icon: <i className="bi bi-people-fill"></i>,
      name: "Organizational Structure",
      url: "/organizational-structure",
    },
    userManagement: {
      icon: <i className="bi bi-people-fill"></i>,
      name: "User Management",
      url: "/user-management",
    },
    emissionInventory: {
      icon: <FactoryIcon />,
      name: "My Emission Inventory",
      url: "/emission-view",
      arrowIcon: <i className="bi bi-arrow-right-circle-fill"></i>,
    },
    dataQualityManagement: {
      icon: <QualityIcon />,
      name: "Data Quality Management",
      url: "/data-quality-management",
    },
    uncertaintyAssessment: {
      icon: <UncertaintyIcon />,
      name: "Uncertainty Assessment",
      url: "/uncertainty-assessment",
    },
    dashboard: {
      icon: <InsertChartIcon />,
      name: "My Dashboard",
      url: "/dashboard",
    },
    emissionFactorManagement: {
      icon: <i className="bi bi-book-half"></i>,
      name: "Emission Factor Management",
      url: "/emmision-factor-classification",
    },
    boundarySettings: {
      icon: <ViewInArOutlinedIcon />,
      name: "Boundary Settings",
      url: "/boundary-setting",
    },
    subsidiariesList: {
      icon: <i className="bi bi-file-earmark-text"></i>,
      name: "Subsidiaries List",
      url: "/child-company-list",
    },
    facilitiesList: {
      icon: <i className="bi bi-file-earmark-text-fill"></i>,
      name: "Facilities List",
      url: "/facility-list",
    },
    reportGeneration: {
      icon: <i className="bi bi-file-earmark-text-fill"></i>,
      name: "Report Generation",
      url: "/report-generation",
    },
    operatorList: {
      icon: <i className="bi bi-list-columns"></i>,
      name: "Operator List",
      url: "/operator-list",
    },
    customEmissionFactorList: {
      icon: <i className="bi bi-bell-fill"></i>,
      name: "Custom Emission Factor List",
      url: "/custom-emission-factor-list",
    },
    translationManagement: {
      icon: <i className="bi bi-translate"></i>,
      name: "Translation Management",
      url: "/translation-management",
    },
    auditorVerification: {
      icon: <VerifiedUserIcon />,
      name: "Online Verification", //Auditor Verification
      url: "/auditor-verification",
    },
    onlineVerification: {
      icon: <VerifiedUserIcon />,
      name: "Online Verification",
      url: "/online-verification",
    },
    //大專院校-盤查指引
    guidanceForEmissionActivities: {
      icon: <MapIcon />,
      name: "Data Collection",
      url: "/guidance-for-emission-activity",
    },
    //大專院校-排放活動總覽
    emissionActivityOverview: {
      // icon: <OverviewIcon />,
      icon: <DvrIcon />,
      name: "Emission Activity Overview",
      url: "/emission-activity-overview",
    },
    //User Activity Log
    activityLog: {
      icon: <HistoryIcon />,
      name: "Activity Log",
      url: "/activity-log",
    },
  };

  // 角色菜單配置
  const roleMenus = {
    system_admin: [
      "home",
      "userManagement",
      "emissionFactorManagement",
      "subsidiariesList",
      "facilitiesList",
      "operatorList",
      "customEmissionFactorList",
      "translationManagement",
      "activityLog",
    ],
    super_admin: [
      "organizationalStructure",
      "boundarySettings",
      "emissionInventory",
      "emissionActivityOverview",
      "dataQualityManagement",
      "uncertaintyAssessment",
      "dashboard",
      "emissionFactorManagement",
      "reportGeneration",
      "activityLog",
      //"onlineVerification",
    ],
    senior_admin: [
      "organizationalStructure",
      "boundarySettings",
      "emissionInventory",
      "emissionActivityOverview",
      "dataQualityManagement",
      "uncertaintyAssessment",
      "dashboard",
      "emissionFactorManagement",
      "reportGeneration",
      "activityLog",
      //"onlineVerification",
    ],
    admin: [
      "emissionInventory",
      "emissionActivityOverview",
      "dataQualityManagement",
      "uncertaintyAssessment",
      "dashboard",
      "emissionFactorManagement",
      "activityLog",
    ],
    operator: [
      "emissionInventory",
      "emissionActivityOverview",
      "dataQualityManagement",
      "uncertaintyAssessment",
      "dashboard",
      "emissionFactorManagement",
      "activityLog",
    ],
    auditor: ["auditorVerification"],
  };

  // 根據角色生成菜單數據
  //沒有industryCode的版本
  // const generateMenuData = (userRole) => {
  //   const menuKeys = roleMenus[userRole] || [];
  //   return menuKeys.map((key) => menuItems[key]).filter(Boolean);
  // };
  // 根據角色生成菜單數據
  const generateMenuData = (userRole) => {
    let menuKeys = [...(roleMenus[userRole] || [])];

    // super_admin、admin、operator 都顯示 Data Collection，進頁面後再判斷 industryCode
    const guidanceItem = "guidanceForEmissionActivities";
    if (userRole === "super_admin") {
      menuKeys.splice(2, 0, guidanceItem);
    } else if (userRole === "admin" || userRole === "operator") {
      menuKeys.unshift(guidanceItem);
    }

    return menuKeys.map((key) => menuItems[key]).filter(Boolean);
  };

  // 根據角色選擇對應的 Sidebar 項目
  const Data = generateMenuData(role);

  // 使用 useLocation 監聽路由變化
  useEffect(() => {
    const currentPath = location.pathname;
    const matchedItem = Data.find((item) => item.url === currentPath);

    if (!matchedItem) {
      // 如果當前路徑不在 Sidebar 選項中，清除選中狀態
      setSelectedName("");
    } else {
      // 如果匹配到項目，設定為選中
      setSelectedName(matchedItem.name);
    }
  }, [location.pathname]); // 監聽 location.pathname 變化

  //登入後第一個畫面
  const getDefaultSelectedName = (userRole) => {
    switch (userRole) {
      case "auditor":
        return "Online Verification"; // 審計員預設到審計頁面
      case "system_admin":
        return "Landing Page"; // 系統管理員預設到首頁
      case "super_admin":
      case "senior_admin":
      case "admin":
      case "operator":
      default:
        // return "My Emission Inventory"; // 其他角色預設到排放清單
        return "My Dashboard"; //先改到Dashboard
    }
  };

  useEffect(() => {
    setSelectedName(getDefaultSelectedName(role));
  }, [role]);

  return (
    <div
      className={`${styles.container} ${
        authenticated ? styles.visible : styles.hidden
      }`}
    >
      {/* Sidebar 容器 */}
      <div className={styles.sidebarContainer}>
        {Data?.map((v, i) => (
          <div
            key={i}
            className={
              location.pathname === v.url // 直接比對當前路徑
                ? styles.menuItemSelected
                : styles.menuItem
            }
            onClick={() => handleItemClick(v)}
          >
            <div
              className={`${styles.sidebarContent} ${
                v.name === "My Dashboard" ? styles.dropdown : ""
              }`}
            >
              {v.type === "img" ? (
                <img
                  src={v.img}
                  className={styles.iconImage}
                  //   style={getIconImageStyle(v.img)}
                  alt=""
                />
              ) : (
                <div className={styles.sidebarIcon}>{v.icon}</div>
              )}

              <div className={styles.sidebarText}>{t(v.name)}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
});

export default SideBarv4;
